import React from 'react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { useIsNarrowTripCard } from 'Modules/MultiStopWizard/Components/Leg/Contexts/useLegOptionContext';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';

interface Props {
	/** How many sailings are folded away. */
	count: number;
	/** The departed sailings themselves, drawn only once the group is opened. */
	children: React.ReactNode;
}

/**
 * The sailings that have already gone today, folded into one line.
 *
 * <p>Staff can book a departed sailing, so these rows cannot simply be dropped — but on a day that
 * is half over they are the first thing on the list and the bookable sailings start below the fold.
 * Collapsed, the day opens on what can still be sold and the past is one press away.</p>
 *
 * <p><b>Only where something is left.</b> The caller does not build this group on a day whose every
 * sailing has gone — see `FilteredTripCards`. On a past day the departed sailings are not clutter
 * in front of the list, they <i>are</i> the list, and folding them away would hide the whole day
 * behind a button.</p>
 */
export default function TripOptionDepartedGroup({ count, children }: Props) {
	const [expanded, setExpanded] = React.useState(false);
	/*
		The band is a row of the desktop table and a card on a phone.

		Square and flush is right between table rows, and wrong above a column of detached cards —
		it welded itself to the first sailing with no gap and no corners. The modifier is taken the
		same way every other piece of this screen takes it, so the two cannot disagree about which
		width they are drawing for.
	*/
	const isNarrow = useIsNarrowTripCard();

	return (
		<>
			<button
				type="button"
				className={classNames('trip-option-departed', {
					'trip-option-departed--mobile': isNarrow,
				})}
				data-test-id="trip-option-departed-group"
				aria-expanded={expanded}
				onClick={() => setExpanded(value => !value)}
			>
				<Icon
					name={expanded ? 'expand_less' : 'expand_more'}
					useMaterialIcons
					classname="trip-option-departed__caret"
				/>
				<span className="trip-option-departed__label">
					<FormattedMessage
						id="trip-option__departed-group__label"
						defaultMessage="{count, plural, one {# departed trip} other {# departed trips}}"
						values={{ count }}
					/>
				</span>
			</button>

			{expanded && children}
		</>
	);
}
